import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { AssetManager } from '../../../../../viewer/asset-manager';
import { InputHandler } from '../../../../../viewer/input-handler';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { MeasureTool } from '../../../../../viewer/tools/measure-tool';
import { PoiTool } from '../../../../../viewer/tools/poi-tool';
import { SelectTool } from '../../../../../viewer/tools/select-tool';
import { MenuTooltipTool } from '../../../../../viewer/tools/tooltip-helper';
import { AssetSnapSettings } from '../../../../../viewer/tools/utils/asset-snapping';
import { useFeatureFlag } from '../../../../hooks/use-feature-flag';
import { useShowAllHeights } from '../../../../hooks/use-show-all-heights';
import { useStoredSetting } from '../../../../hooks/use-stored-setting';
import { useSubscribeCallback } from '../../../../hooks/use-subscribe';
import { CheckBox } from '../../../check-box/check-box';
import { Dialog } from '../../../dialog/dialog';
import { Icon } from '../../../icon/icon';
import { SnapAssetsDialog } from '../../../skyview/right-menu/tools/design-tool-menu/dialogs/snap-assets-dialog';
import { MenuTooltip } from '../../../skyview/right-menu/tools/design-tool-menu/menu-tooltip';
import { SnapStyleButtons } from '../../../snap-style-buttons/snap-style-buttons';
import { getBesideAssetMenuPosition } from '../../asset-menu/dialogs/asset-dialog-position';
import { SnapToSection } from './snap-to-section';
import { HelpBand, SnappingHelp, SnappingHelpContext, StylesBar, useHelp } from './snapping-sheet';
import { useFitsInWindow } from './use-fits-in-window';

/**
 * The tool whose snapping the dialog sets, and what identifies it: the active tool, or the
 * selected line or area whose points are moved. Undefined when there is nothing to snap.
 */
const getSnappingTarget = () => {
  const tool = InputHandler.instance.getSnappingTool();
  const key = InputHandler.instance.isSelectToolActive()
    ? AssetManager.instance.getSelected()?.uuid
    : tool?.name;

  return tool && isDefined(key) ? { tool, key } : undefined;
};

/**
 * The snapping of the measure tool, the POI tool and of moving the points of a line or an area,
 * with the flag on.
 */
export const SnappingDialog = () => {
  const enabled = useFeatureFlag('SK-7558-measure-snapping-options');
  return enabled ? <AutoOpeningSnappingDialog /> : null;
};

/**
 * Opens by itself beside the asset menu as a tool that it sets the snapping of is used, and closes
 * when it is no longer used. It can be closed, and opens again for another tool, or as another
 * line or area is selected.
 */
const AutoOpeningSnappingDialog = () => {
  // The tool and its key are kept together, so the dialog always shows the tool it changes.
  const [target, setTarget] = React.useState(getSnappingTarget);
  const [visible, setVisible] = React.useState(isDefined(target));

  const update = () => {
    const newTarget = getSnappingTarget();

    // Not when the tool is only set again, e.g. after a measurement is saved.
    if (newTarget?.key !== target?.key) {
      setTarget(newTarget);
      setVisible(isDefined(newTarget));
    }
  };

  useSubscribeCallback(SubscriptionTopic.SetTool, update);
  useSubscribeCallback(SubscriptionTopic.NewSelection, update);
  useSubscribeCallback(SubscriptionTopic.UnSelection, update);

  return visible && target ? (
    <SnappingDialogContent key={target.key} tool={target.tool} onClose={() => setVisible(false)} />
  ) : null;
};

const SnappingDialogContent = (props: {
  tool: MeasureTool | PoiTool | SelectTool;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { snapping } = props.tool;
  const { settings, excludedSnapAssetIds } = React.useSyncExternalStore(
    snapping.subscribe,
    snapping.getSnapshot,
  );
  const [allHeights, setAllHeights] = useStoredSetting('snappingDialogAllHeights');
  const allHeightsEnabled = useFeatureFlag('SK-7558-visualize-all-heights-option');
  useShowAllHeights(props.tool.heightsGuide, allHeightsEnabled && allHeights);
  const [showSnapAssetsDialog, setShowSnapAssetsDialog] = React.useState(false);

  const onSettingsChanged = (newValue: Partial<AssetSnapSettings>) =>
    snapping.onSettingsChanged(newValue);

  return (
    <>
      <SnappingDialogFrame
        settings={
          allHeightsEnabled && (
            <SettingCheck
              checked={allHeights}
              help={{
                title: t('section.options.allHeights.tooltip.title', { ns: 'skyviewDesign' }),
                text: t('section.options.allHeights.tooltip.text', { ns: 'skyviewDesign' }),
              }}
              label={t('section.options.allHeights.text', { ns: 'skyviewDesign' })}
              onChange={() => setAllHeights(!allHeights)}
            />
          )
        }
        tool={props.tool}
        onClose={props.onClose}
      >
        <StylesBar>
          <SnapStyleButtons
            value={settings.styles}
            onChange={(styles) => onSettingsChanged({ styles })}
          />
        </StylesBar>

        <SnapToSection
          absoluteHeight={settings.absoluteHeight}
          assets={settings.assets}
          customAssets={excludedSnapAssetIds.size > 0}
          height={settings.height}
          heightOffset={settings.heightOffset}
          pointCloud={settings.pointCloud}
          tiledModel={settings.tiledModel}
          onAbsoluteHeightChanged={(absoluteHeight) => onSettingsChanged({ absoluteHeight })}
          onChooseAssets={() => setShowSnapAssetsDialog(true)}
          onHeightChanged={(height) => onSettingsChanged({ height })}
          onHeightOffsetChanged={(heightOffset) => onSettingsChanged({ heightOffset })}
          onToggle={(target) => onSettingsChanged({ [target]: !settings[target] })}
        />
      </SnappingDialogFrame>
      {showSnapAssetsDialog && (
        <SnapAssetsDialog tool={snapping} onClose={() => setShowSnapAssetsDialog(false)} />
      )}
    </>
  );
};

/**
 * A setting of the dialog, behind the cog in its header, with its help in the band.
 */
const SettingCheck = (props: {
  checked: boolean;
  label: string;
  help: SnappingHelp;
  onChange: () => void;
}) => (
  <span {...useHelp(props.help)}>
    <CheckBox checked={props.checked} onChange={props.onChange}>
      {props.label}
    </CheckBox>
  </span>
);

/**
 * The snapping dialog of a tool, beside the asset menu: its snapping options, its settings behind
 * the cog in its header, and the tooltip of the tool in a band at the bottom while that setting is
 * on. The band shows the help of the control under the pointer instead, see SnappingHelpContext.
 * For the measure tool, the POI tool, moving the points of a line or an area, and the design tool.
 */
export const SnappingDialogFrame = (props: {
  tool: MenuTooltipTool;
  /** The snapping options, the snap styles first, see StylesBar. */
  children: React.ReactNode;
  /** Settings of the tool behind the cog, after the tooltip's, e.g. to show all heights. */
  settings?: React.ReactNode;
  /** @default 280 */
  width?: number;
  /** Shows the tooltip of the tool again, to move it into the dialog at once, see MenuTooltip. */
  refreshTooltip?: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const tooltipInMenuEnabled = useFeatureFlag('SK-7540-tooltip-in-menu');
  const [tooltipInDialog, setTooltipInDialog] = useStoredSetting('snappingDialogTooltip');
  // Hidden whenever the dialog opens, until they're needed
  const [settingsShown, setSettingsShown] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(false);
  const [position] = React.useState(() => getBesideAssetMenuPosition());
  const [tooltipElement, setTooltipElement] = React.useState<HTMLDivElement | null>(null);
  const [help, setHelp] = React.useState<SnappingHelp>();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const hasSettings = tooltipInMenuEnabled || !!props.settings;
  const tooltipShown = tooltipInMenuEnabled && tooltipInDialog;

  return (
    <SnappingHelpContext.Provider value={setHelp}>
      <Dialog
        // Beside the asset menu, below the other dialogs there, e.g. the point list of the line
        // or area whose points are moved.
        autoDock={true}
        closeIcon={true}
        collapsible={true}
        // The rows have it, so that their lines and the tooltip's band reach the dialog's edges
        contentPadding={false}
        disableDimmer={true}
        headerIcons={
          hasSettings && (
            <Icon
              aria-expanded={settingsShown}
              aria-label={t('snappingDialog.settings', { ns: 'skyviewDesign' })}
              fixedWidth={true}
              icon={[settingsShown ? 'fas' : 'fal', 'gear']}
              title={t('snappingDialog.settings', { ns: 'skyviewDesign' })}
              onClick={() => setSettingsShown(!settingsShown)}
            />
          )
        }
        // As high as the window has room for, see useFitsInWindow
        maxHeight={false}
        movable={true}
        position={position}
        width={props.width ?? 280}
        onClose={props.onClose}
        onCollapsedChanged={setCollapsed}
      >
        {{
          header: t('section.snapping.title', { ns: 'skyviewDesign' }),
          content: (
            <FittingBody
              // At the bottom, below the options, which scroll above it, so that they don't move
              // as it grows while measuring. Not while collapsed, when it would be hidden: then it
              // is next to the cursor. The help of the control under the pointer takes its place,
              // which stays mounted, so that the tooltip doesn't move next to the cursor meanwhile.
              band={
                !collapsed &&
                (tooltipShown || help) && (
                  <Band ref={setTooltipElement}>
                    {help && <HelpBand help={help} />}
                    {tooltipShown && (
                      <DialogTooltip
                        hidden={!!help}
                        refresh={props.refreshTooltip}
                        title={t('menuTooltip.title', { ns: 'skyviewDesign' })}
                        tool={props.tool}
                      />
                    )}
                  </Band>
                )
              }
              following={tooltipElement}
              scrollRef={scrollRef}
            >
              {hasSettings && settingsShown && (
                <Settings>
                  {tooltipInMenuEnabled && (
                    <SettingCheck
                      checked={tooltipInDialog}
                      help={{
                        title: t('snappingDialog.tooltipInDialog.tooltip.title', {
                          ns: 'skyviewDesign',
                        }),
                        text: t('snappingDialog.tooltipInDialog.tooltip.text', {
                          ns: 'skyviewDesign',
                        }),
                      }}
                      label={t('snappingDialog.tooltipInDialog.text', { ns: 'skyviewDesign' })}
                      onChange={() => setTooltipInDialog(!tooltipInDialog)}
                    />
                  )}
                  {props.settings}
                </Settings>
              )}

              <Options>{props.children}</Options>
            </FittingBody>
          ),
        }}
      </Dialog>
    </SnappingHelpContext.Provider>
  );
};

/**
 * The dialog's content, which scrolls where the window it is in is too low for it, with the band
 * below it, which stays in view, see useFitsInWindow. Within the dialog, which tells it its window.
 */
const FittingBody = (props: {
  scrollRef: React.RefObject<HTMLDivElement>;
  /** Below the content, outside of what scrolls. */
  band: React.ReactNode;
  following: HTMLElement | null;
  children: React.ReactNode;
}) => {
  useFitsInWindow(props.scrollRef, props.following);
  return (
    <Body>
      <Scroll ref={props.scrollRef}>{props.children}</Scroll>
      {props.band}
    </Body>
  );
};

// As high as the dialog where it fills a window of its own, so that the band is at its bottom there
const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

// Fills what the band leaves, scrolling what doesn't fit
const Scroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Band = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
`;

// The rows reach the dialog's edges, see SheetRow
const Options = styled.div`
  flex: none;
  padding-bottom: 6px;
`;

// The settings behind the cog in the header, above the options
const Settings = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  padding: 6px calc(var(--padding) + 0.5em);
  border-bottom: 1px solid ${(props) => props.theme.color.gray.light};
  background-color: #f7f8f8;
`;

// Down to the bottom of the dialog
const DialogTooltip = styled(MenuTooltip)`
  flex: 1 0 auto;
`;
